import CONFIG from '../../../../config';
import { searchByName } from '../../../searchHelpers';

const CONTEXT_AUDIT_MAX_TEXT_CHARS = 900;
const AUDIT_STOPWORD_TOKENS = new Set([
  'about', 'also', 'are', 'can', 'could', 'describe', 'did', 'does', 'do', 'each', 'explain', 'from', 'give',
  'how', 'into', 'list', 'show', 'tell', 'that', 'them', 'they', 'this', 'those', 'what', 'when', 'where',
  'which', 'who', 'why', 'will', 'with', 'would', 'your'
]);
const GENERIC_TASK_TOKENS = new Set([
  'agree', 'analysis', 'analyze', 'check', 'comment', 'comments', 'critique', 'evaluate', 'feedback',
  'opinion', 'opinions', 'provide', 'read', 'react', 'reaction', 'response', 'responses', 'review', 'summarize',
  'summary', 'thought', 'thoughts', 'understand', 'whether'
]);
const GENERIC_REFERENCE_TOKENS = new Set([
  'attached', 'attachment', 'context', 'document', 'documents', 'file', 'files', 'linked', 'provided',
  'uploaded'
]);
const BROAD_SCOPE_TARGET_PATTERN = /\b(?:all\s+docs?|all\s+documents|codebase|entire\s+project|everything|folder|project|repo(?:sitory)?|workspace)\b/i;
const ATTACHMENT_REFERENCE_PATTERN = /\b(?:attached|attachment|linked|provided|uploaded)\b|\b(?:this|that)\s+(?:doc|document|file|attachment|context)\b/i;
const DEMONSTRATIVE_REFERENCE_PATTERN = /\b(?:this|that|these|those)\b/i;

const toFiniteNumber = (value, fallback = 0) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : fallback;
};

const tokenizeForAudit = (value = '') => {
  return Array.from(new Set(
    String(value || '')
      .toLowerCase()
      .replace(/[^a-z0-9\s]+/g, ' ')
      .split(/\s+/)
      .map((token) => token.trim())
      .filter((token) => token.length >= 4)
      .filter((token) => !AUDIT_STOPWORD_TOKENS.has(token))
  ));
};

export const analyzeImplicitTargetPrompt = ({ userText = '' } = {}) => {
  const normalizedText = String(userText || '').trim();
  const userKeywords = tokenizeForAudit(normalizedText).slice(0, 20);
  const substantiveKeywords = userKeywords.filter((token) => {
    return !GENERIC_TASK_TOKENS.has(token) && !GENERIC_REFERENCE_TOKENS.has(token);
  });
  const broadScopeRequested = BROAD_SCOPE_TARGET_PATTERN.test(normalizedText);
  const hasAttachmentReference = ATTACHMENT_REFERENCE_PATTERN.test(normalizedText);
  const hasDemonstrativeReference = DEMONSTRATIVE_REFERENCE_PATTERN.test(normalizedText);
  const hasGenericOnlyKeywords = userKeywords.length > 0 && substantiveKeywords.length === 0;
  const demonstrativeWithAction = hasDemonstrativeReference && userKeywords.length > 0 && hasGenericOnlyKeywords;

  return {
    userKeywords,
    substantiveKeywords,
    broadScopeRequested,
    hasAttachmentReference,
    hasDemonstrativeReference,
    hasGenericOnlyKeywords,
    demonstrativeWithAction,
    shouldAssumeAttachmentTarget: Boolean(
      normalizedText
      && !broadScopeRequested
      && (demonstrativeWithAction || hasGenericOnlyKeywords || (userKeywords.length === 0 && hasDemonstrativeReference))
    )
  };
};

const buildKeywordTokenItems = (value = '') => {
  return Array.from(new Set(
    String(value || '')
      .toLowerCase()
      .replace(/[^a-z0-9\s]+/g, ' ')
      .split(/\s+/)
      .map((token) => token.trim())
      .filter((token) => token.length >= 3)
  )).map((name) => ({ name }));
};

const fuzzyKeywordMatch = ({ keyword = '', tokenItems = [] } = {}) => {
  const query = String(keyword || '').trim().toLowerCase();
  if (!query || !Array.isArray(tokenItems) || tokenItems.length === 0) return false;

  const minPrefixLength = query.length >= 8 ? 5 : 4;
  const prefix = query.slice(0, minPrefixLength);
  if (!prefix) return false;
  const hasPrefixCandidate = tokenItems.some((item) => {
    const token = String(item?.name || '').trim().toLowerCase();
    return token.length >= minPrefixLength && token.startsWith(prefix);
  });
  if (!hasPrefixCandidate) return false;

  const results = searchByName(tokenItems, query, {
    key: 'name',
    limit: 1,
    fuzzyThreshold: Number(CONFIG.SEARCH?.NAME_SEARCH?.FUZZY_THRESHOLD || 0.25)
  });
  return Array.isArray(results) && results.length > 0;
};

export const summarizeContextReferences = (items = [], maxPreviewItems = 5) => {
  const rows = Array.isArray(items) ? items : [];
  return rows.slice(0, Math.max(1, Number(maxPreviewItems || 5))).map((item) => {
    const text = String(item?.text || '').replace(/\s+/g, ' ').trim();
    return {
      source: item?.source || 'unknown',
      docId: item?.meta?.docId ?? null,
      label: item?.meta?.docName || item?.meta?.label || null,
      preview: text ? `${text.slice(0, 180)}${text.length > 180 ? '...' : ''}` : ''
    };
  });
};

export const summarizeSourceBreakdown = (items = []) => {
  const breakdown = {};
  for (const item of Array.isArray(items) ? items : []) {
    const source = String(item?.source || 'unknown').trim() || 'unknown';
    breakdown[source] = Number(breakdown[source] || 0) + 1;
  }
  return breakdown;
};

export const summarizeDocSelectionBreakdown = (items = [], limit = 6) => {
  const map = new Map();
  for (const item of Array.isArray(items) ? items : []) {
    const docId = Number(item?.meta?.docId || 0);
    if (docId <= 0) continue;
    const key = String(docId);
    const existing = map.get(key) || {
      docId,
      docName: item?.meta?.docName || item?.meta?.label || null,
      count: 0,
      chars: 0,
      sources: new Set()
    };
    existing.count += 1;
    existing.chars += String(item?.text || '').length;
    existing.sources.add(String(item?.source || 'unknown').trim() || 'unknown');
    if (!existing.docName) {
      existing.docName = item?.meta?.docName || item?.meta?.label || null;
    }
    map.set(key, existing);
  }

  return Array.from(map.values())
    .map((entry) => ({
      docId: entry.docId,
      docName: entry.docName,
      count: entry.count,
      chars: entry.chars,
      sources: Array.from(entry.sources)
    }))
    .sort((left, right) => {
      if (right.count !== left.count) return right.count - left.count;
      return right.chars - left.chars;
    })
    .slice(0, Math.max(1, Number(limit || 6)));
};

export const clipAuditText = (value = '', maxChars = CONTEXT_AUDIT_MAX_TEXT_CHARS) => {
  const text = String(value || '').replace(/\s+/g, ' ').trim();
  const limit = Math.max(120, Number(maxChars || CONTEXT_AUDIT_MAX_TEXT_CHARS));
  if (!text) return '';
  return text.length <= limit ? text : `${text.slice(0, Math.max(0, limit - 3)).trimEnd()}...`;
};

export const summarizeContextItemsForAudit = (items = [], maxItems = 10) => {
  return (Array.isArray(items) ? items : [])
    .slice(0, Math.max(1, Number(maxItems || 10)))
    .map((item) => ({
      source: String(item?.source || 'unknown').trim() || 'unknown',
      score: toFiniteNumber(item?.score, null),
      baseScore: toFiniteNumber(item?.baseScore, null),
      recencyBoost: toFiniteNumber(item?.recencyBoost, null),
      truncatedByBudget: Boolean(item?.truncated),
      docId: item?.meta?.docId ?? null,
      docName: item?.meta?.docName || item?.meta?.label || null,
      chunkHash: item?.meta?.chunkHash || null,
      contextRole: item?.meta?.contextRole || null,
      text: clipAuditText(item?.text || '')
    }));
};

export const buildContextKeywordCoverage = ({ userText = '', items = [] } = {}) => {
  const userKeywords = tokenizeForAudit(userText).slice(0, 20);
  const joinedContext = (Array.isArray(items) ? items : [])
    .map((item) => String(item?.text || ''))
    .join(' ');
  const contextLower = joinedContext.toLowerCase();
  const contextTokenItems = buildKeywordTokenItems(joinedContext);
  if (userKeywords.length === 0) {
    return {
      userKeywords: [],
      matchedKeywords: [],
      missingKeywords: [],
      coverageRatio: 1
    };
  }

  const matchedKeywords = userKeywords.filter((token) => {
    const normalized = String(token || '').toLowerCase();
    if (!normalized) return false;
    if (contextLower.includes(normalized)) return true;
    return fuzzyKeywordMatch({ keyword: normalized, tokenItems: contextTokenItems });
  });
  const missingKeywords = userKeywords.filter((token) => !matchedKeywords.includes(token));
  const coverageRatio = Number((matchedKeywords.length / userKeywords.length).toFixed(3));

  return {
    userKeywords,
    matchedKeywords,
    missingKeywords,
    coverageRatio
  };
};